import { useCallback, useEffect, useRef, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { setFilterField } from "../../app/itemGridPreferencesSlice";
import APIs from "../../Constants/apis";
import axiosInstance from "../../config/axiosApi/axiosApi";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast from "../../components/CustomComponents/toast/toast";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import moment from "moment";
import Pagination from "../../components/CustomComponents/Pagination/pagination";
import "./../order/ManageOrderFilters.css";

const activityCount = (row, camel, pascal) => row[camel] ?? row[pascal] ?? 0;

const toValidDate = (v) => {
  if (v == null) return null;
  const d = typeof v === "string" ? new Date(v) : v;
  return d instanceof Date && !isNaN(d.getTime()) ? d : null;
};

const StaffActivityLogReport = () => {
  const dispatch = useDispatch();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;

  const pageKey = "staffActivityLogReport";
  const defaultLocation = { value: "all", label: "All Locations" };
  const defaultDateRange = [
    moment().startOf("week").toDate(),
    moment().endOf("week").toDate(),
  ];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const location = preferences.location || defaultLocation;
  const storedDateRange = preferences.dateRange;
  const dateRange = (() => {
    if (!storedDateRange || !Array.isArray(storedDateRange)) return defaultDateRange;
    const start = toValidDate(storedDateRange[0]);
    const end = toValidDate(storedDateRange[1]);
    if (!start && !end) return defaultDateRange;
    return [
      start || moment().startOf("week").toDate(),
      end || moment().endOf("week").toDate(),
    ];
  })();
  const [rawStartDate, rawEndDate] = dateRange;

  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "20", label: "20" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const initRanRef = useRef(false);

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const themeColor = useThemeColor();

  const parentIdForLocation = isMain && CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
  const allLocationsOption = { value: "all", label: "All Locations" };
  const locationOptions =
    locations.length > 0
      ? [allLocationsOption, ...locations]
      : isMain && parentIdForLocation != null && !isNaN(parentIdForLocation)
        ? [allLocationsOption, { value: parentIdForLocation, label: companyName }]
        : [allLocationsOption];

  const dateRangeKey =
    rawStartDate && rawEndDate
      ? `${moment(rawStartDate).format("YYYY-MM-DD")}-${moment(rawEndDate).format("YYYY-MM-DD")}`
      : "";

  const locationValue = location?.value ?? "all";

  const handleFilterChange = (filterName, selectedOption) => {
    dispatch(setFilterField({ pageKey, field: filterName, value: selectedOption }));
    setPageNumber(1);
  };

  const resetFilters = () => {
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    setSearch("");
    setAppliedSearch("");
  };

  const clearAllFilters = () => {
    resetFilters();
    setPageNumber(1);
  };

  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, { params: { CompID } });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const compName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
      const base = [{ value: parentId, label: compName }];
      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          return pidNum === parentId && idNum !== parentId;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${id ?? ""}`;
          const val = typeof id === "number" ? id : parseInt(id, 10);
          return { value: isNaN(val) ? null : val, label: name };
        })
        .filter((o) => o.value != null);
      setLocations([...base, ...branchOpts]);
    } catch (error) {
      console.error("Error fetching locations:", error);
      showErrorToast(error.response?.data?.message || "Error loading locations");
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, profileData?.compName, profileData?.CompName]);

  const fetchReport = useCallback(async () => {
    if (!CompID || !dateRangeKey) return;
    setLoading(true);
    try {
      const startDate = dateRangeKey.slice(0, 10);
      const endDate = dateRangeKey.slice(11);
      if (!startDate || !endDate) {
        showErrorToast("Please select a start and end date.");
        setRows([]);
        setTotalRecords(0);
        setTotalPages(0);
        return;
      }

      let locationFilterValue = null;
      if (isMain && locationValue !== "all" && locationValue != null) {
        const locValue = typeof locationValue === "number" ? locationValue : parseInt(locationValue, 10);
        if (!isNaN(locValue) && locValue > 0) locationFilterValue = locValue;
      }

      const params = {
        CompID,
        StartDate: startDate,
        EndDate: endDate,
        LocationFilter: locationFilterValue,
        Search: appliedSearch || null,
        PageNumber: pageNumber,
        PageSize: pageSize.value,
      };
      const response = await axiosInstance.get(APIs.GET_STAFF_ACTIVITY_SUMMARY_REPORT, { params });
      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Failed to load report");
        setRows([]);
        setTotalRecords(0);
        setTotalPages(0);
        return;
      }

      const payload = response.data.data || {};
      const data = payload.data ?? payload.Data ?? [];
      setRows(Array.isArray(data) ? data : []);
      const total = payload.totalRecords ?? payload.TotalRecords ?? 0;
      const pages = payload.totalPages ?? payload.TotalPages ?? 0;
      setTotalRecords(total);
      setTotalPages(pages);
    } catch (error) {
      console.error("Staff activity log report:", error);
      showErrorToast(error.response?.data?.message || "Failed to load report");
      setRows([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [CompID, dateRangeKey, locationValue, appliedSearch, pageNumber, pageSize.value, isMain]);

  useEffect(() => {
    if (initRanRef.current) return;
    initRanRef.current = true;
    if (!preferences.location) dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    const needsDefaultDate =
      !storedDateRange ||
      !Array.isArray(storedDateRange) ||
      !toValidDate(storedDateRange[0]) ||
      !toValidDate(storedDateRange[1]);
    if (needsDefaultDate) dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
  }, [dispatch, pageKey]);

  useEffect(() => {
    if (isMain) fetchLocations();
  }, [isMain, fetchLocations]);

  useEffect(() => {
    const t = setTimeout(() => setAppliedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleDateRangeChange = (dates) => {
    const [startDate, endDate] = Array.isArray(dates) ? dates : [dates, null];
    let effectiveStart = startDate ? (typeof startDate === "string" ? new Date(startDate) : startDate) : null;
    let effectiveEnd = endDate ? (typeof endDate === "string" ? new Date(endDate) : endDate) : null;
    if (effectiveStart && effectiveEnd && effectiveStart > effectiveEnd) {
      [effectiveStart, effectiveEnd] = [effectiveEnd, effectiveStart];
    } else if (effectiveStart && !effectiveEnd) {
      effectiveEnd = moment(effectiveStart).endOf("day").toDate();
      effectiveStart = moment(effectiveStart).startOf("day").toDate();
    }
    dispatch(
      setFilterField({
        pageKey,
        field: "dateRange",
        value: [effectiveStart, effectiveEnd],
      })
    );
    setPageNumber(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPageNumber(1);
  };

  const displayName = (row) => {
    const fn = row.first_Name ?? row.First_Name ?? "";
    const ln = row.last_Name ?? row.Last_Name ?? "";
    const full = `${fn} ${ln}`.trim();
    return full || row.userName || row.UserName || "";
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <a href="#/report">Report</a>
            </li>
            <li className="breadcrumb-item active">Staff activity log report</li>
          </ol>
          <h1 className="page-header mb-0">Staff activity log report</h1>
        </div>
      </div>

      <div
        className="card mb-4"
        style={{
          position: "relative",
          zIndex: 10,
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: "12px",
          border: "none",
          boxShadow: `0 4px 20px ${themeColor}40`,
        }}
      >
        <div className="card-body p-3">
          <div className="row g-3 align-items-center mb-3">
            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="filter-control" style={{ position: "relative", zIndex: 9999, width: "100%" }}>
                <DateRangePicker
                  startDate={toValidDate(rawStartDate)}
                  endDate={toValidDate(rawEndDate)}
                  onChange={handleDateRangeChange}
                  placeholder="Select Date Range"
                  className="filter-dropdown"
                  allowSingleDate={true}
                  allowDateRange={true}
                />
              </div>
            </div>
            <div className="col-xl-7 col-lg-5 col-md-6">
              <div className="search-input filter-control">
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search by username or name..."
                  value={search}
                  onChange={handleSearchChange}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "#333",
                  }}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-3 col-md-12">
              <div className="d-flex gap-2 justify-content-end">
                <button className="clear-btn" onClick={clearAllFilters} title="Clear all filters" type="button">
                  <i className="fa fa-times me-1"></i>
                  Filter
                </button>
              </div>
            </div>
          </div>
          <div className="row g-3 align-items-center">
            {isMain && locationOptions.length > 1 && (
              <div className="col-xl-2 col-lg-4 col-md-6">
                <div className="filter-control">
                  <Select
                    value={location}
                    onChange={(option) => handleFilterChange("location", option)}
                    options={locationOptions}
                    styles={dropdownStyles}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    placeholder="Location"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuShouldBlockScroll={false}
                    closeMenuOnScroll={false}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div
              className="table-responsive mt-3"
              style={{ minHeight: "200px", maxHeight: "450px", overflowY: "auto", overflowX: "auto" }}
            >
              <table className="table table-hover">
                <thead
                  className="bg-light"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    <th className="pt-2 pb-2">Staff</th>
                    <th className="pt-2 pb-2">Username</th>
                    <th className="pt-2 pb-2">Role</th>
                    <th className="pt-2 pb-2 text-end">Logins</th>
                    <th className="pt-2 pb-2 text-end">Settings opens</th>
                    <th className="pt-2 pb-2 text-end">Open printer</th>
                    <th className="pt-2 pb-2 text-end">Open sale report</th>
                    <th className="pt-2 pb-2 text-end">Open general report</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && !loading && (
                    <tr>
                      <td colSpan={8} className="text-center py-4 text-muted">
                        No staff activity found for the selected filters.
                      </td>
                    </tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.userName ?? row.UserName}>
                      <td className="pt-2 pb-2">{displayName(row)}</td>
                      <td className="pt-2 pb-2">{row.userName ?? row.UserName ?? ""}</td>
                      <td className="pt-2 pb-2">{row.roleName ?? row.RoleName ?? ""}</td>
                      <td className="pt-2 pb-2 text-end">{activityCount(row, "loginCount", "LoginCount")}</td>
                      <td className="pt-2 pb-2 text-end">{activityCount(row, "settingsOpenCount", "SettingsOpenCount")}</td>
                      <td className="pt-2 pb-2 text-end">{activityCount(row, "printerSettingsCount", "PrinterSettingsCount")}</td>
                      <td className="pt-2 pb-2 text-end">{activityCount(row, "saleReportCount", "SaleReportCount")}</td>
                      <td className="pt-2 pb-2 text-end">{activityCount(row, "generalReportCount", "GeneralReportCount")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 0 && (
              <Pagination
                totalRecords={totalRecords}
                pageSize={pageSize}
                pageNumber={pageNumber}
                totalPages={totalPages}
                setPageNumber={setPageNumber}
                setPageSize={(opt) => {
                  setPageSize(opt);
                  setPageNumber(1);
                }}
                themeColor={themeColor}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffActivityLogReport;
